import { API_ENDPOINTS } from '../endpoints';
import { getAuthHeaders, handleResponse } from '../client';
import type {
  PageResponse,
  ProjectCreateDTO,
  ProjectOperationLogQueryParams,
  ProjectOperationLogQueryResultVO,
  ProjectTerminateDTO,
  ProjectUpdateDTO,
  ProjectVO,
} from '../types';

export const projectApi = {
  getList: async (params?: {
    pageNum?: number;
    pageSize?: number;
    brand?: string;
    /** 项目状态，逗号分隔多选；不传则不过滤 */
    status?: string;
    keyword?: string;
    code?: string;
    launchDateStart?: string;
    launchDateEnd?: string;
    creatorIds?: number[];
  }): Promise<PageResponse<ProjectVO>> => {
    const queryParams = new URLSearchParams();
    if (params?.pageNum) queryParams.append('pageNum', params.pageNum.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.brand) queryParams.append('brand', params.brand);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.keyword) queryParams.append('keyword', params.keyword);
    if (params?.code) queryParams.append('code', params.code);
    if (params?.launchDateStart) queryParams.append('launchDateStart', params.launchDateStart);
    if (params?.launchDateEnd) queryParams.append('launchDateEnd', params.launchDateEnd);
    if (params?.creatorIds?.length) queryParams.append('creatorIds', params.creatorIds.join(','));

    const response = await fetch(`${API_ENDPOINTS.PROJECTS.LIST}?${queryParams}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<PageResponse<ProjectVO>>(response);
  },

  getById: async (id: number): Promise<ProjectVO> => {
    const response = await fetch(API_ENDPOINTS.PROJECTS.DETAIL(id), {
      headers: getAuthHeaders(),
    });
    return handleResponse<ProjectVO>(response);
  },

  create: async (data: ProjectCreateDTO): Promise<ProjectVO> => {
    const response = await fetch(API_ENDPOINTS.PROJECTS.CREATE, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<ProjectVO>(response);
  },

  update: async (id: number, data: ProjectUpdateDTO): Promise<boolean> => {
    const response = await fetch(API_ENDPOINTS.PROJECTS.UPDATE(id), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<boolean>(response);
  },

  replaceIpdProjects: async (id: number, ipdProjectIds: number[]): Promise<ProjectVO> => {
    const response = await fetch(API_ENDPOINTS.PROJECTS.REPLACE_IPD(id), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ ipdProjectIds }),
    });
    return handleResponse<ProjectVO>(response);
  },

  delete: async (id: number): Promise<boolean> => {
    const response = await fetch(API_ENDPOINTS.PROJECTS.DELETE(id), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    // 后端成功时可能返回 res_content: ""，不应按 boolean 解析
    await handleResponse<unknown>(response);
    return true;
  },

  terminate: async (id: number, data: ProjectTerminateDTO): Promise<ProjectVO> => {
    const response = await fetch(API_ENDPOINTS.PROJECTS.TERMINATE(id), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<ProjectVO>(response);
  },

  /** 项目操作日志（IBS 多 ID 聚合 + 后端筛选）；支持 AbortSignal */
  getOperationLog: async (
    id: number,
    params?: ProjectOperationLogQueryParams,
    signal?: AbortSignal,
  ): Promise<ProjectOperationLogQueryResultVO> => {
    const q = new URLSearchParams();
    if (params?.startTime) q.append('startTime', params.startTime);
    if (params?.endTime) q.append('endTime', params.endTime);
    if (params?.operationTypes) q.append('operationTypes', params.operationTypes);
    if (params?.operators) q.append('operators', params.operators);
    if (params?.objectTypes) q.append('objectTypes', params.objectTypes);
    if (params?.objectKeyword) q.append('objectKeyword', params.objectKeyword);
    if (params?.changeKeyword) q.append('changeKeyword', params.changeKeyword);
    const qs = q.toString();
    const url = qs
      ? `${API_ENDPOINTS.PROJECTS.OPERATION_LOG(id)}?${qs}`
      : API_ENDPOINTS.PROJECTS.OPERATION_LOG(id);
    const response = await fetch(url, {
      headers: getAuthHeaders(),
      signal,
    });
    const data = await handleResponse<ProjectOperationLogQueryResultVO | ProjectOperationLogQueryResultVO['items'] | null>(
      response,
    );
    // 兼容旧数组响应
    if (Array.isArray(data)) {
      return { items: data, operators: [] };
    }
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      operators: Array.isArray(data?.operators) ? data.operators : [],
    };
  },
};

/** 品牌列表（来自 brand_tbl，新建项目下拉用；保存项目时传 brand_code） */
